import { List } from "immutable"
import { Action, any, async, fromJSX, loadedAsyncState, loadingAsyncState, nothing } from "widgets-for-react"
import { previousOrDefault } from "../../../../custom_api/api_utils"
import { AdviceFilter, AdviceSortMethod, CollectionType, FilterData, FilterValue, ValueType } from "../../../../custom_api/bookarang/types"
import { fetchAdvice, fetchBookWiseDetails } from "../../../../custom_api/collection/api"
import { WiseBook, WiseBookDetails } from "../../../../custom_api/wise/types"
import { BranchState } from "../../application"
import { pushInspirationAction } from "../../datalayer"
import { CollectionPageLoaderWidget } from "../Shared/InspirationUtils"
import { AdviceLayoutKiosk } from "./AdviceLayoutKiosk"
import { AdviceLayoutWeb } from "./AdviceLayoutWeb"
import { AdviceLayoutProps, AdviceProps, AdviceState, BookHighlight, HighlightState, initialHighlightState } from "./AdviceState"
import { sortLabels } from "./FilterSelector/FilterSelectorState"
import debounce = require("lodash.debounce")
import QRCode = require('qrcode')
import { Availability, CollectionItem } from "../../../../../Shared/backendApi/generated"
import { fetchAvailability } from "../../../../../Shared/backendApi/WiseCollectionApi"

type Updater = (_: Action<AdviceState>) => void

export const AdviceWidget = (props: AdviceProps) => {
  return any<Action<AdviceState>>({ key: `Advice` })([
    fromJSX((setState) => {
      const branchId = props.branch != 'none' && props.branch.Id
      const layoutProps: AdviceLayoutProps = {
        ...props,
        highlight: toHighlightProps(props),
        updateFilterValue: (id: number, name: string, value: FilterValue) => {
          updateFilterValue(setState, id, value)
          debouncedUpdateAdviceByFilter(setState, id, value)
          debouncedDataLayerAction(props.adviceType, `Filter ${name}`, value.toString())
        },
        updateSortMethod: (method: AdviceSortMethod) => {
          pushInspirationAction(props.adviceType, 'Sorteer advies', sortLabels[method])
          updateSortMethodValue(setState, method),
            debouncedUpdateAdviceBySort(setState, method)
        },
        setHighlight: (bookId: string) => { setHighlight(setState, bookId, branchId) },
        resetHighlight: () => setState(s0 => ({ ...s0, ...initialHighlightState }))
      }
      return props.isKioskMode
        ? AdviceLayoutKiosk(layoutProps)
        : AdviceLayoutWeb(layoutProps)
    }),

    async<WiseBook[]>(nothing, { key: 'AdviceLoader' })(props.advice)
      .map(resultUpdater => s0 => {
        const advice = resultUpdater(s0.advice)
        return ({ ...s0, advice })
      }),
    async<AdviceFilter[]>(nothing, { key: 'AdviceFiltersLoader' })(props.filters)
      .map(resultUpdater => s0 => {
        const filters = resultUpdater(s0.filters)
        return ({ ...s0, filters })
      }),
    async<WiseBookDetails>(nothing, { key: 'HighlightDetailsLoader' })(props.highlightedBookDetails)
      .map(resultUpdater => s0 => {
        const highlightedBookDetails = resultUpdater(s0.highlightedBookDetails)
        return ({ ...s0, highlightedBookDetails })
      }),
    async<List<Availability>>(nothing, { key: 'HighlightAvailabilityLoader' })(props.highlightedBookAvailability)
      .map(resultUpdater => s0 => {
        const highlightedBookAvailability = resultUpdater(s0.highlightedBookAvailability)
        return ({ ...s0, highlightedBookAvailability })
      }),
    async<CollectionItem>(nothing, { key: 'SelectedBookLoader' })(props.selectedBook)
      .map(resultUpdater => s0 => {
        const selectedBook = resultUpdater(s0.selectedBook)

        return ({ ...s0, selectedBook })
      }),
    CollectionPageLoaderWidget(props)
  ]).map(a => a(props))
}

const getFilterName = (filters: AdviceFilter[], id: number) => filters.find((f) => f.id === id).name

const setHighlight = (setState: Updater, bookId: string, branchId: string) => {
  setState(s0 => {
    if (s0.advice.kind != 'loaded') return s0
    const selectedBook = s0.selectedBook.kind == 'loaded' && s0.selectedBook.value
    const highlightedBook: WiseBook | undefined = selectedBook && selectedBook.OriginalId == bookId
      ? {
        id: selectedBook.OriginalId,
        title: selectedBook.title,
        author: selectedBook.authors?.[0],
        cover: selectedBook.imageUrls?.['w80'].replace('size=80&', '')
      } as WiseBook
      : s0.advice.value.find(b => b.id == bookId)

    if (highlightedBook) setHighlightQR(setState, highlightedBook.id, branchId)

    return {
      ...s0,
      highlightedBook,
      highlightedBookDetails: highlightedBook
        ? loadingAsyncState(fetchBookWiseDetails(bookId))
        : s0.highlightedBookDetails,
      highlightedBookAvailability: highlightedBook
        ? loadingAsyncState(fetchAvailability(bookId))
        : s0.highlightedBookAvailability
    }
  })
}

const setHighlightQR = (setState: Updater, id: string, branchId: string) => {
  let url = `${window.location.origin}/t/reserveer?id=${id}`
  if (branchId) url = `${url}&b=${branchId}`
  QRCode.toDataURL(url, {
    errorCorrectionLevel: 'H',
    margin: 0,
    scale: 5,
    color: { dark: '#000000ff', light: "#ffffffff" }
  }).then(
    (dataUrl) => setState(s0 => ({ ...s0, highlightedBookQR: dataUrl })),
    (reason) => {
      console.warn(`Generating QR code for WiseID ${id} failed.`, reason)
    }
  )
}

const updateSortMethodValue = (setState: Updater, method: AdviceSortMethod) => setState(s0 => ({ ...s0, sortMethod: method }))

const updateAdviceByFilter = (setState: Updater, id: number, value: FilterValue) => {
  setState(s0 => {
    if (s0.filters.kind != 'loaded') return { ...s0 }
    let searchParams = updateQueryParams(`filter_${getFilterName(s0.filters.value, id)}`, value)
    return newAdviceState(s0, searchParams)
  })
}

const updateAdviceBySort = (setState: Updater, sortMethod: AdviceSortMethod) => {
  setState(s0 => {
    let searchParams = updateQueryParams('sort', sortMethod)
    return newAdviceState(s0, searchParams)
  })
}

const newAdviceState = (s0: AdviceState, searchParams: URLSearchParams): AdviceState => {
  return {
    ...s0,
    advice: loadingAsyncState(fetchAdvice(s0.adviceType, `${s0.selectedPrompt}?${searchParams.toString()}`), previousOrDefault(s0.advice, [])),
    ...initialHighlightState
  }
}

const updateFilterValue = (setState: Updater, id: number, value: FilterValue) => {
  setState(s0 => {
    if (s0.filters.kind != 'loaded') return { ...s0 }
    const intermediate: AdviceFilter[] = s0.filters.value.map(f => {
      if (f.id != id) return f
      const metadata = setFilterDataValue(f.metadata, value)
      return { ...f, metadata }
    })
    const filters = loadedAsyncState(intermediate)
    return { ...s0, filters }
  })
}

const debouncedDataLayerAction = debounce((collection: CollectionType, action: string, label: string) => pushInspirationAction(collection, action, label), 1000)

const debouncedUpdateAdviceByFilter = debounce(updateAdviceByFilter, 200)
const debouncedUpdateAdviceBySort = debounce(updateAdviceBySort, 50)

const setFilterDataValue: (data: FilterData, newValue: FilterValue) => FilterData =
  (data, newValue) => {
    if (data.feature_type == 'float') {
      const value = newValue as ValueType<typeof data.feature_type>
      return { ...data, value }
    }
    if (data.feature_type == 'boolean') {
      const value = newValue as ValueType<typeof data.feature_type>
      return { ...data, value }
    }
    return data
  }

const updateQueryParams = (paramName: string, value: unknown) => {
  const newValue = value.toString()
  let searchParams = new URLSearchParams(window.location.search)
  searchParams.set(paramName, encodeURIComponent(newValue))
  var newRelativePathQuery = window.location.pathname + '?' + searchParams.toString()
  history.pushState(null, '', newRelativePathQuery)
  return searchParams
}

const toHighlightProps: (state: HighlightState) => BookHighlight = (state: HighlightState & BranchState) => {
  const { highlightedBook, highlightedBookAvailability: copies, highlightedBookDetails: details, highlightedBookQR: qrDataUrl, branch } = state
  const description = details.kind == 'loaded' && (details.value.acquisitionInformation?.trim() || highlightedBook.description)
  const book = { ...highlightedBook, description }

  // No book has been selected, so no details can be shown
  if (!highlightedBook) return null

  // We don't know what the availability is yet
  if (copies.kind != 'loaded') return { book, availability: 'unknown' }

  // Offer option to reserve as no copies are currently available
  const availableCopies = copies.value.filter(a => a.EffectiveStatus == 'AVAILABLE')
  if (availableCopies.isEmpty()) return { book, qrDataUrl, availability: 'none' }

  // Offer option to reserve as copies are available, but we don't know where the user is
  if (branch == 'none') return { book, qrDataUrl, availability: 'somewhere' }

  // Tell the user where a copy can be found at their current location
  const localCopy = availableCopies.filter(a => a.BranchId == branch.Id).first()
  if (localCopy) return { book, availability: 'here', location: localCopy.Location, code: localCopy.CallNumber }

  // Offer option to reserve as copies are available at other locations
  return { book, availability: 'elsewhere', qrDataUrl }
}
